// Vista principal de Inspecciones (F3a): tabla tipo Monday (estado, plantilla, sede, responsable, fecha programada o
// realizada, hallazgos abiertos y vencidos), con filtros y agrupación por estado o por sede, y vista de calendario.
// La sede (columna, filtro y agrupación) solo aparece con 2 o más sedes activas (regla de F1a). En celular la tabla
// pasa a tarjetas y el calendario muestra el número de inspecciones por día.

import React, { useMemo, useState } from 'react';
import {
  agruparFilas, Agrupacion, EstadoVisible, fechaEc, filasTabla, filtrarFilas, FILTROS_VACIOS, FiltrosTabla, isoLocal, porFecha, semanasDelMes,
} from '../../lib/inspecciones/insp-logica';
import type { ActivoMin, FilaHallazgo, FilaInspeccion, SedeMin, TrabajadorMin } from '../../lib/inspecciones/insp-tipos';
import { ETIQUETA_ESTADO_INSPECCION } from '../../lib/inspecciones/insp-tipos';
import SelectBuscable from '../../components/SelectBuscable';
import { Campo } from '../pe/ComponentesPE';
import { plural } from '../../lib/plurales';

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'];
const ESTADOS: EstadoVisible[] = ['atrasada', 'programada', 'en_curso', 'completada', 'cancelada'];

export function ChipEstado({ estado }: { estado: string }) {
  return <span className={`insp-chip ${estado}`}>{(ETIQUETA_ESTADO_INSPECCION as Record<string, string>)[estado] ?? estado}</span>;
}

export interface PropsTabla {
  inspecciones: FilaInspeccion[];
  hallazgos: FilaHallazgo[];
  sedes: SedeMin[];
  varias: boolean;
  trabajadores: TrabajadorMin[];
  /** Activos del inventario: la columna "Activo" muestra el vinculado o "—". */
  activos?: ActivoMin[];
  onAbrir: (id: string) => void;
  hoy: Date;
}

export default function TablaInspecciones(p: PropsTabla) {
  const [vista, setVista] = useState<'tabla' | 'calendario'>('tabla');
  const [filtros, setFiltros] = useState<FiltrosTabla>(FILTROS_VACIOS);
  const [agrupar, setAgrupar] = useState<Agrupacion>('ninguna');
  const [mes, setMes] = useState(() => ({ anio: p.hoy.getFullYear(), mes: p.hoy.getMonth() }));
  const [dia, setDia] = useState<string | null>(null);

  const filas = useMemo(() => filasTabla(p.inspecciones, p.hallazgos, p.sedes, p.trabajadores, p.hoy, p.activos),
    [p.inspecciones, p.hallazgos, p.sedes, p.trabajadores, p.hoy, p.activos]);
  const visibles = useMemo(() => filtrarFilas(filas, p.varias ? filtros : { ...filtros, sede: '' }), [filas, filtros, p.varias]);
  const grupos = useMemo(() => agruparFilas(visibles, !p.varias && agrupar === 'sede' ? 'ninguna' : agrupar), [visibles, agrupar, p.varias]);
  const plantillas = useMemo(() => Array.from(new Set(filas.map(f => f.plantilla))).sort(), [filas]);
  const delMes = useMemo(() => porFecha(visibles), [visibles]);
  const hoyIso = isoLocal(p.hoy);

  const fila = (f: typeof filas[number]) => (
    <div key={f.id} className="insp-fila" role="row">
      <div role="cell"><span className="insp-et">Estado</span><ChipEstado estado={f.estado} /></div>
      <div role="cell"><span className="insp-et">Plantilla</span>{f.plantilla}{f.area ? <small className="insp-ayuda"> · {f.area}</small> : null}</div>
      <div role="cell"><span className="insp-et">Activo</span>{f.activo}</div>
      {p.varias && <div role="cell"><span className="insp-et">Sede</span>{f.sede}</div>}
      <div role="cell"><span className="insp-et">Responsable</span>{f.responsable}</div>
      <div role="cell"><span className="insp-et">{f.realizada ? 'Realizada' : 'Programada'}</span>{fechaEc(f.fecha)}</div>
      <div role="cell"><span className="insp-et">Hallazgos abiertos</span>{f.abiertos}</div>
      <div role="cell"><span className="insp-et">Vencidos</span><span className={f.vencidos ? 'insp-num-alerta' : undefined}>{f.vencidos}</span></div>
      <div role="cell"><button type="button" className="pe2-btn" aria-label={`Abrir ${f.plantilla} del ${fechaEc(f.fecha)}`} onClick={() => p.onAbrir(f.id)}>Abrir</button></div>
    </div>
  );

  return (
    <section className="pe2-card" aria-label="Inspecciones">
      <div className="pe2-cabecera">
        <h3>Inspecciones</h3>
        <div className="pe2-vistas" role="tablist" aria-label="Vista">
          <button type="button" role="tab" aria-selected={vista === 'tabla'} className={vista === 'tabla' ? 'on' : ''} onClick={() => setVista('tabla')}>Tabla</button>
          <button type="button" role="tab" aria-selected={vista === 'calendario'} className={vista === 'calendario' ? 'on' : ''} onClick={() => setVista('calendario')}>Calendario</button>
        </div>
      </div>
      <div className="insp-filtros">
        <Campo etiqueta="Estado">
          <select value={filtros.estado} onChange={e => setFiltros({ ...filtros, estado: e.target.value as EstadoVisible | '' })}>
            <option value="">Todos</option>
            {ESTADOS.map(s => <option key={s} value={s}>{ETIQUETA_ESTADO_INSPECCION[s]}</option>)}
          </select>
        </Campo>
        <Campo etiqueta="Plantilla">
          <SelectBuscable value={filtros.plantilla} vacio="Todas" onChange={v => setFiltros({ ...filtros, plantilla: v })}
            opciones={plantillas.map(n => ({ value: n, label: n }))} />
        </Campo>
        {p.varias && (
          <Campo etiqueta="Sede">
            <SelectBuscable value={filtros.sede} vacio="Todas" onChange={v => setFiltros({ ...filtros, sede: v })}
              opciones={p.sedes.filter(s => s.active).map(s => ({ value: s.id, label: s.name }))} />
          </Campo>
        )}
        <Campo etiqueta="Buscar">
          <input type="text" value={filtros.texto} placeholder="Plantilla, área o responsable" onChange={e => setFiltros({ ...filtros, texto: e.target.value })} />
        </Campo>
        {vista === 'tabla' && (
          <Campo etiqueta="Agrupar por">
            <select value={agrupar} onChange={e => setAgrupar(e.target.value as Agrupacion)}>
              <option value="ninguna">Sin agrupar</option>
              <option value="estado">Estado</option>
              {p.varias && <option value="sede">Sede</option>}
            </select>
          </Campo>
        )}
      </div>

      {vista === 'tabla' ? (
        visibles.length === 0 ? <p className="pe2-nota">{filas.length ? 'Ninguna inspección coincide con los filtros.' : 'Todavía no hay inspecciones. Crea una nueva o prográmala.'}</p> : (
          <div className={`insp-tabla${p.varias ? '' : ' sin-sede'}`} role="table" aria-label="Lista de inspecciones">
            <div className="insp-fila cabeza" role="row">
              <span role="columnheader">Estado</span><span role="columnheader">Plantilla</span><span role="columnheader">Activo</span>
              {p.varias && <span role="columnheader">Sede</span>}
              <span role="columnheader">Responsable</span><span role="columnheader">Fecha</span><span role="columnheader">Abiertos</span>
              <span role="columnheader">Vencidos</span><span role="columnheader"><span className="pe2-nota">Acción</span></span>
            </div>
            {grupos.map(g => (
              <React.Fragment key={g.clave || 'todas'}>
                {g.clave && <div className="insp-grupo" role="row"><span role="cell">{agrupar === 'estado' ? ETIQUETA_ESTADO_INSPECCION[g.clave as EstadoVisible] : g.clave} ({g.filas.length})</span></div>}
                {g.filas.map(fila)}
              </React.Fragment>
            ))}
          </div>
        )
      ) : (
        <div className="pe2-columna">
          <div className="insp-cal-nav">
            <button type="button" className="pe2-btn" aria-label="Mes anterior" onClick={() => { setDia(null); setMes(m => (m.mes === 0 ? { anio: m.anio - 1, mes: 11 } : { ...m, mes: m.mes - 1 })); }}>‹</button>
            <b>{MESES[mes.mes]} {mes.anio}</b>
            <button type="button" className="pe2-btn" aria-label="Mes siguiente" onClick={() => { setDia(null); setMes(m => (m.mes === 11 ? { anio: m.anio + 1, mes: 0 } : { ...m, mes: m.mes + 1 })); }}>›</button>
          </div>
          <div className="insp-cal" aria-label={`Calendario de ${MESES[mes.mes]} ${mes.anio}`}>
            {DIAS.map(d => <div key={d} className="dia-semana">{d}</div>)}
            {semanasDelMes(mes.anio, mes.mes).flat().map((f, k) => {
              if (!f) return <div key={`v${k}`} />;
              const lista = delMes.get(f) ?? [];
              return (
                <div key={f}>
                  <button type="button" className={`dia${f === hoyIso ? ' hoy' : ''}${dia === f ? ' on' : ''}`} onClick={() => setDia(f)}
                    aria-label={`${Number(f.slice(8))} de ${MESES[mes.mes]}: ${plural(lista.length, 'inspección', 'inspecciones')}`}>
                    <span>{Number(f.slice(8))}</span>
                    {lista.length > 0 && <span className="n">{lista.length}</span>}
                    {lista.slice(0, 2).map(x => <span key={x.id} className="t">{x.plantilla}</span>)}
                  </button>
                </div>
              );
            })}
          </div>
          {dia && (
            <div className="pe2-columna" aria-label={`Inspecciones del ${fechaEc(dia)}`} role="region">
              <h4>{fechaEc(dia)}</h4>
              {(delMes.get(dia) ?? []).length === 0 ? <p className="pe2-nota">Sin inspecciones este día.</p>
                : <div className={`insp-tabla${p.varias ? '' : ' sin-sede'}`} role="table" aria-label="Inspecciones del día">{(delMes.get(dia) ?? []).map(fila)}</div>}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
